import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { ClinicSettings } from '@/lib/clinic/types'

import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { ImageUp, Save, Trash2 } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SectionCard } from '@/components/app/common'
import { FormField, preventEnterSubmit } from '@/components/app/form-bits'
import { BrandMark } from '@/components/app/brand-mark'

type ClinicForm = ClinicSettings['clinic']

const MAX_LOGO_SIZE = 256

/** Shrinks an uploaded logo so it stays small enough to keep in browser storage. */
const readLogo = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onerror = () => reject(new Error('read-failed'))
        reader.onload = () => {
            const source = String(reader.result)

            if (file.type === 'image/svg+xml') {
                if (source.length > 200000) reject(new Error('too-large'))
                else resolve(source)

                return
            }

            const image = new Image()

            image.onerror = () => reject(new Error('not-an-image'))
            image.onload = () => {
                const ratio = Math.min(1, MAX_LOGO_SIZE / Math.max(image.width, image.height))
                const canvas = document.createElement('canvas')

                canvas.width = Math.max(1, Math.round(image.width * ratio))
                canvas.height = Math.max(1, Math.round(image.height * ratio))

                const context = canvas.getContext('2d')

                if (!context) {
                    reject(new Error('canvas-unavailable'))

                    return
                }

                context.drawImage(image, 0, 0, canvas.width, canvas.height)
                resolve(canvas.toDataURL('image/png'))
            }

            image.src = source
        }

        reader.readAsDataURL(file)
    })
}

export const ClinicSettingsPanel: FC = (): ReactNode => {
    const { data, updateSettings } = useClinic()
    const [form, setForm] = useState<ClinicForm>(data.settings.clinic)
    const [error, setError] = useState<string | null>(null)
    const fileRef = useRef<HTMLInputElement>(null)
    const dirty = JSON.stringify(form) !== JSON.stringify(data.settings.clinic)

    const set = (key: keyof ClinicForm, value: string | null): void => {
        setForm(previous => ({ ...previous, [key]: value }))
    }

    const uploadLogo = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = event.target.files?.[0]

        event.target.value = ''

        if (!file) return

        if (!file.type.startsWith('image/')) {
            toast.error('Choose an image file', { description: 'PNG, JPG, WEBP or SVG work best.' })

            return
        }

        try {
            set('logo', await readLogo(file))
        }
        catch {
            toast.error("Couldn't use this image", { description: 'Try a smaller PNG or JPG file.' })
        }
    }

    const submit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        if (!form.name.trim()) {
            setError('Enter the clinic name.')

            return
        }

        setError(null)
        updateSettings(settings => ({ ...settings, clinic: { ...form, name: form.name.trim() } }))
        toast.success('Clinic information saved', { description: 'New prescriptions and messages use these details.' })
    }

    return (
        <form onSubmit={submit} onKeyDown={preventEnterSubmit} noValidate className='grid gap-6'>
            <SectionCard title='Clinic logo' description='Shown in the sidebar and on printed prescriptions.'>
                <div className='flex flex-wrap items-center gap-4'>
                    <BrandMark name={form.name} logo={form.logo} className='size-20 rounded-2xl' />

                    <div className='grid gap-2'>
                        <div className='flex flex-wrap gap-2'>
                            <Button type='button' variant='outline' onClick={() => fileRef.current?.click()}>
                                <ImageUp />
                                {form.logo ? 'Replace logo' : 'Upload logo'}
                            </Button>

                            {form.logo && (
                                <Button type='button' variant='ghost' onClick={() => set('logo', null)}>
                                    <Trash2 />
                                    Remove
                                </Button>
                            )}
                        </div>

                        <p className='text-xs text-muted-foreground'>
                            A square PNG with a transparent or white background looks best. It is resized automatically.
                        </p>
                    </div>

                    <input ref={fileRef} type='file' accept='image/png,image/jpeg,image/webp,image/svg+xml' className='sr-only' onChange={event => void uploadLogo(event)} aria-label='Upload clinic logo' />
                </div>
            </SectionCard>

            <SectionCard title='Clinic and doctor' description='Printed in the prescription header.'>
                <div className='grid gap-5 sm:grid-cols-2'>
                    <FormField label='Clinic name' htmlFor='clinic-name' required error={error}>
                        <Input id='clinic-name' value={form.name} onChange={event => set('name', event.target.value)} aria-invalid={Boolean(error)} />
                    </FormField>

                    <FormField label='Tagline' htmlFor='clinic-tagline' hint='A short line under the clinic name.'>
                        <Input id='clinic-tagline' value={form.tagline} onChange={event => set('tagline', event.target.value)} />
                    </FormField>

                    <FormField label='Doctor name' htmlFor='clinic-doctor'>
                        <Input id='clinic-doctor' value={form.doctorName} onChange={event => set('doctorName', event.target.value)} />
                    </FormField>

                    <FormField label='Doctor qualification' htmlFor='clinic-qualification'>
                        <Input id='clinic-qualification' value={form.qualification} onChange={event => set('qualification', event.target.value)} placeholder='e.g. BDS, MDS' />
                    </FormField>

                    <FormField label='Registration number' htmlFor='clinic-registration' hint='Optional. Printed under the doctor name.'>
                        <Input id='clinic-registration' value={form.registrationNo} onChange={event => set('registrationNo', event.target.value)} />
                    </FormField>

                    <FormField label='Working hours' htmlFor='clinic-hours'>
                        <Input id='clinic-hours' value={form.hours} onChange={event => set('hours', event.target.value)} placeholder='e.g. Mon to Sat, 9:30 AM to 7:30 PM' />
                    </FormField>

                    <FormField label='Clinic address' htmlFor='clinic-address' className='sm:col-span-2'>
                        <Textarea id='clinic-address' value={form.address} onChange={event => set('address', event.target.value)} rows={2} />
                    </FormField>

                    <FormField label='Clinic phone number' htmlFor='clinic-phone'>
                        <Input id='clinic-phone' type='tel' value={form.phone} onChange={event => set('phone', event.target.value)} />
                    </FormField>

                    <FormField label='Email' htmlFor='clinic-email' hint='Optional'>
                        <Input id='clinic-email' type='email' value={form.email} onChange={event => set('email', event.target.value)} />
                    </FormField>
                </div>
            </SectionCard>

            <SaveBar dirty={dirty} onReset={() => setForm(data.settings.clinic)} />
        </form>
    )
}

interface SaveBarProps {
    dirty: boolean
    onReset: () => void
}

export const SaveBar: FC<SaveBarProps> = ({ dirty, onReset }): ReactNode => {
    return (
        <div className='sticky bottom-0 z-10 -mx-4 flex items-center justify-end gap-2 border-t bg-white/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border'>
            <p className='mr-auto text-sm text-muted-foreground'>
                {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
            </p>

            {dirty && (
                <Button type='button' variant='ghost' onClick={onReset}>
                    Discard
                </Button>
            )}

            <Button type='submit' disabled={!dirty}>
                <Save />
                Save changes
            </Button>
        </div>
    )
}
